import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import { TbArrowRight } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { BaasDashboardSkeleton } from "../components/BaasSkeleton";
import {
  buildBaasCustodyHierarchyFromPeerBalances,
  fetchBaasAccountHierarchy,
  fetchBaasPeerInstanceBalances,
} from "../../../../Services/baasAccountsApi";
import {
  computeEntityTotals,
  fetchBaasEntities,
} from "../../../../Services/baasEntidadesApi";
import { BAAS_CARTOES_PATH } from "../constants";
import { shouldShowBaasCustodyAggregate } from "../baasInstanceListFilter";
import {
  applyBaasCurrencyFilters,
  getBaasDisplayCurrency,
} from "./baasCurrencyFilters";
import {
  EMPTY_BAAS_HOME_ALERTS,
  fetchBaasHomeAlerts,
} from "./baasHomeDashboardApi";
import { getBaasHomeCurrencyFilters } from "./baasHomeCurrency";
import { BaasHomeAlerts } from "./components/BaasHomeAlerts";
import { BaasHomeCardsPanel } from "./components/BaasHomeCardsPanel";
import { BaasHomeCreditoPanel } from "./components/BaasHomeCreditoPanel";
import { BaasHomeProductSummaries } from "./components/BaasHomeProductSummaries";
import { BaasHomeQuickActions } from "./components/BaasHomeQuickActions";
import { BaasProcessingSummaryCards } from "./components/BaasProcessingSummaryCards";

const EMPTY_HIERARCHY = { clientName: "", rootAccount: null, subAccounts: [] };
const HOME_CURRENCY_FILTERS = getBaasHomeCurrencyFilters("BRL");

export const BaasHomePage = () => {
  const { selectedInstance, dataEpoch, listInstanceFilter } = useBaasInstance();
  const instanceKey = selectedInstance?.id ?? null;
  const { can } = useBaasCapabilities();
  const isLoading = useBaasPageLoading(
    `${instanceKey || "none"}:${listInstanceFilter || "valut"}:${dataEpoch}`,
  );
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [hierarchy, setHierarchy] = useState(EMPTY_HIERARCHY);
  const [custodyHierarchy, setCustodyHierarchy] = useState(EMPTY_HIERARCHY);
  const [entityTotals, setEntityTotals] = useState(computeEntityTotals([]));
  const [homeAlerts, setHomeAlerts] = useState(EMPTY_BAAS_HOME_ALERTS);
  const [dataLoading, setDataLoading] = useState(false);
  const issuerAllowed = can("issuer.panel.view");
  const showCustody = shouldShowBaasCustodyAggregate(
    selectedInstance,
    listInstanceFilter,
  );

  // Reset dismiss when the selected instance changes.
  useEffect(() => {
    setAlertDismissed(false);
  }, [instanceKey]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance || !instanceKey) {
      setHierarchy(EMPTY_HIERARCHY);
      setCustodyHierarchy(EMPTY_HIERARCHY);
      setEntityTotals(computeEntityTotals([]));
      setHomeAlerts(EMPTY_BAAS_HOME_ALERTS);
      setDataLoading(false);
      return undefined;
    }

    // Clear previous instance data immediately so UI never shows stale balances.
    setHierarchy(EMPTY_HIERARCHY);
    setCustodyHierarchy(EMPTY_HIERARCHY);
    setEntityTotals(computeEntityTotals([]));
    setHomeAlerts(EMPTY_BAAS_HOME_ALERTS);
    setDataLoading(true);

    (async () => {
      try {
        const [nextHierarchy, entities, alerts, peerBalances] = await Promise.all([
          fetchBaasAccountHierarchy(selectedInstance),
          fetchBaasEntities(selectedInstance).catch(() => []),
          fetchBaasHomeAlerts(selectedInstance).catch(() => EMPTY_BAAS_HOME_ALERTS),
          showCustody
            ? fetchBaasPeerInstanceBalances(selectedInstance).catch(() => [])
            : Promise.resolve([]),
        ]);
        if (!cancelled) {
          setHierarchy(nextHierarchy);
          setEntityTotals(computeEntityTotals(entities));
          setHomeAlerts(alerts);
          setCustodyHierarchy(
            showCustody
              ? buildBaasCustodyHierarchyFromPeerBalances(peerBalances)
              : EMPTY_HIERARCHY,
          );
        }
      } catch {
        if (!cancelled) {
          setHierarchy(EMPTY_HIERARCHY);
          setCustodyHierarchy(EMPTY_HIERARCHY);
          setEntityTotals(computeEntityTotals([]));
          setHomeAlerts(EMPTY_BAAS_HOME_ALERTS);
        }
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, instanceKey, dataEpoch, showCustody]);

  const filteredHierarchy = useMemo(
    () => applyBaasCurrencyFilters(hierarchy, HOME_CURRENCY_FILTERS),
    [hierarchy],
  );
  const displayCurrency = useMemo(
    () => getBaasDisplayCurrency(hierarchy, HOME_CURRENCY_FILTERS),
    [hierarchy],
  );

  const filteredCustodyHierarchy = useMemo(
    () => applyBaasCurrencyFilters(custodyHierarchy, HOME_CURRENCY_FILTERS),
    [custodyHierarchy],
  );

  if (isLoading || dataLoading) {
    return <BaasDashboardSkeleton />;
  }

  return (
    <div
      className="baas-processing min-w-0 space-y-5"
      key={`${instanceKey}:${listInstanceFilter || "valut"}:${dataEpoch}`}
    >
      <header className="min-w-0">
        <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
          {selectedInstance?.name ?? intl.get("baas.nav.home")}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.home.dashboard.subtitle")}
        </p>
      </header>

      <BaasHomeAlerts
        pendingKybCount={entityTotals.pendingKyb}
        pendingCount={entityTotals.pending}
        apiErrorCount={homeAlerts.apiErrorCount}
        webhookFailureCount={homeAlerts.webhookFailureCount}
        dismissed={alertDismissed}
        onDismiss={() => setAlertDismissed(true)}
      />

      <BaasProcessingSummaryCards
        hierarchy={filteredHierarchy}
        displayCurrency={displayCurrency}
      />

      {showCustody ? (
        <BaasProcessingSummaryCards
          hierarchy={filteredCustodyHierarchy}
          displayCurrency={displayCurrency}
          title={intl.get("baas.processing.custodySection")}
        />
      ) : null}

      <BaasHomeQuickActions />

      <BaasHomeProductSummaries displayCurrency={displayCurrency} />

      {/* Crédito e garantias under Emissor — only when issuer product is permitted. */}
      {issuerAllowed ? (
        <section className="min-w-0 w-full space-y-4">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Link
              to={BAAS_CARTOES_PATH}
              className="link-no-zoom inline-flex items-center gap-1 text-xs font-semibold text-[#e7a73f] transition hover:underline dark:text-brand-gold-mid"
            >
              {intl.get("baas.home.dashboard.viewEmissor")}
              <TbArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </div>
          <div className="grid auto-rows-fr items-stretch gap-5 lg:grid-cols-2">
            <div className="flex h-full min-h-0 min-w-0 flex-col">
              <BaasHomeCardsPanel embedded displayCurrency={displayCurrency} />
            </div>
            <div className="flex h-full min-h-0 min-w-0 flex-col">
              <BaasHomeCreditoPanel embedded displayCurrency={displayCurrency} />
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default BaasHomePage;
